import {track} from '../services/journeyEvents';
import React, { useEffect, useRef, useState } from 'react';

import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Container, Stack, Typography } from '@mui/material';

import {authService} from '../services/auth';

import {CaptureBalance} from './CaptureBalance';

import { InsufficientCreditsDialog } from './InsufficientCreditsDialog';

import { CaptureAccountError, FaceJob, faceVideoService } from '../services/captureAccount';

const active = (job: FaceJob) => !['SUCCEEDED', 'REJECTED', 'CANCELLED'].includes(job.state) && (job.state !== 'FAILED' || job.retryable);

export const FaceVideoMocap: React.FC<{onBack: () => void; onLive: () => void; liveEnabled?: boolean; onLogin?:()=>void}> = ({onBack,onLogin}) => {

  const [quote,setQuote]=useState<number|null>(null);

  const [quoting,setQuoting]=useState(false);

  const [file, setFile] = useState<File | null>(null);

  const [jobs, setJobs] = useState<FaceJob[]>([]);

  const jobsRef = useRef<FaceJob[]>([]);

  const [busy, setBusy] = useState(false);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState('');

  const [insufficientCredits, setInsufficientCredits] = useState(false);

  const [downloadId, setDownloadId] = useState('');

  const [submitted, setSubmitted] = useState(false);

  const key = useRef('');

  const update = (rows: FaceJob[]) => {jobsRef.current = rows; setJobs(rows);window.dispatchEvent(new Event('creditsUpdated'));};

  useEffect(() => {

    const controller = new AbortController(); let timer: ReturnType<typeof setTimeout>;

    const refresh = async (initial = false) => {

      try {

        let rows = initial ? await faceVideoService.list(controller.signal) : jobsRef.current;

        const refreshed = await Promise.all(rows.map(async job => active(job) ? faceVideoService.get(job.job_id, controller.signal) : job));

        if (!controller.signal.aborted) {update([...jobsRef.current.filter(j => !refreshed.some(r => r.job_id === j.job_id)), ...refreshed]);}

      } catch (e) {if (!controller.signal.aborted) setError((e as Error).message);}

      finally {if (!controller.signal.aborted) {setLoading(false); timer = setTimeout(() => void refresh(), 5000);}}

    };

    if(authService.isAuthenticated())void refresh(true);

    return () => {controller.abort(); clearTimeout(timer);};

  }, []);

  useEffect(()=>{setQuote(null);setQuoting(false);if(!file)return;const controller=new AbortController();setQuoting(true);faceVideoService.estimate(file,controller.signal).then(value=>{if(!controller.signal.aborted)setQuote(value.estimated_credits);}).catch(e=>{if(!controller.signal.aborted)setError((e as Error).message);}).finally(()=>{if(!controller.signal.aborted)setQuoting(false);});return()=>controller.abort();},[file]);

  const submit = async () => {

    if (!file || busy || quote===null) return;

    setBusy(true); setError('');

    if (!key.current) key.current = crypto.randomUUID();

    try {

      const job = await faceVideoService.submit(file, key.current,quote);

      update([job, ...jobsRef.current.filter(j => j.job_id !== job.job_id)]);

      // Preserve key after unknown delivery so retry cannot enqueue a duplicate.

      setSubmitted(!['SUBMITTING', 'SUBMISSION_UNKNOWN'].includes(job.state));

    } catch (e) {

      if (e instanceof CaptureAccountError && e.status === 402 && authService.isAuthenticated()) {track('paywall_viewed',{surface:'face'});setInsufficientCredits(true);}

      else setError((e as Error).message);

    }

    finally {setBusy(false);}

  };

  const download = async (job: FaceJob) => {

    setDownloadId(job.job_id); setError('');

    try {

      const blob = await faceVideoService.download(job.job_id); const url = URL.createObjectURL(blob);

      const link = document.createElement('a'); link.href = url; link.download = `face-${job.job_id}.json`;

      document.body.appendChild(link); link.click(); link.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);

    } catch (e) {if(e instanceof CaptureAccountError && e.status===401){track('auth_requested',{surface:'face'});onLogin?.();}else if(e instanceof CaptureAccountError && e.status===402){track('paywall_viewed',{surface:'face'});setInsufficientCredits(true);}else setError((e as Error).message);}

    finally {setDownloadId('');}

  };

  if(!authService.isAuthenticated())return <Container maxWidth="md" sx={{py:5}}><Button onClick={onBack}>Back</Button><Typography variant="h3" component="h1" sx={{my:2}}>Face Mocap</Typography><CaptureBalance /><Button variant="contained" onClick={onLogin}>Create an account or sign in</Button></Container>;

  return <Container maxWidth="md" sx={{py: 5}}>

    <Button onClick={onBack}>Back</Button><Typography variant="h3" component="h1" sx={{my: 2}}>Face Mocap</Typography><CaptureBalance />

    <Typography color="text.secondary" paragraph>Upload a recorded video, follow processing, and download your facial animation.</Typography>

    <Typography color="text.secondary" paragraph>1 whole second of input video = 1 credit; incomplete seconds are rounded down. Body and face videos are separate uploads. Credits are debited only after success. Failed or cancelled captures do not consume credits. Purchased credits do not expire.</Typography>

      <Button data-nina-cta="demo" component="a" href="https://www.youtube.com/watch?v=VfssrYsSl4U" target="_blank" rel="noopener noreferrer">Optional body demo: BVH in Blender / MakeHuman Default</Button>

    <Card variant="outlined"><CardContent><Typography variant="h6" gutterBottom>New capture</Typography><Typography paragraph>Use one clearly visible face with good lighting. 1-60 seconds (at least 30 normalized frames) and up to 100 MiB. The video is normalized automatically; the result contains blendshapes and face transforms at 30 FPS in JSON format.</Typography>

      <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} sx={{my: 2}}><Button component="label" variant="outlined" disabled={busy}>Choose video<input hidden aria-label="Choose face video" type="file" accept="video/*,.mp4,.mov,.mkv,.webm,.avi" onChange={e => {const chosen = e.target.files?.[0] || null; setSubmitted(false); key.current = ''; if (chosen && (chosen.size === 0 || chosen.size > 100 * 1024 * 1024)) {setFile(null); setError('Choose a non-empty video up to 100 MiB.');} else {setFile(chosen); setError('');}}} /></Button><Button variant="contained" disabled={!file || busy || submitted || quote===null || quoting} onClick={() => void submit()}>{busy ? 'Uploadingâ€¦' : submitted ? 'Submitted' : 'Send video'}</Button></Stack>

      {quoting && <Typography>Validating video cost...</Typography>}

      {quote!==null && <Typography>Validated video cost: {quote} credits, debited only after success.</Typography>}

      {file && <Typography variant="body2">{file.name} Â· {(file.size / 1024 / 1024).toFixed(1)} MiB</Typography>}

      {busy && <CircularProgress size={24} sx={{mt: 2}} />}

    </CardContent></Card>

    <InsufficientCreditsDialog open={insufficientCredits} onClose={() => setInsufficientCredits(false)} />

    {error && <Alert severity="error" sx={{mt: 2}} action={!authService.isAuthenticated()?<Button onClick={onLogin}>Sign in to continue</Button>:undefined}>{error}</Alert>}

    <Typography variant="h5" sx={{mt: 4, mb: 2}}>Capture history</Typography>

    {loading && <CircularProgress size={24} />}

    {!loading && jobs.length === 0 && <Typography color="text.secondary">Your submitted videos will appear here.</Typography>}

    <Stack spacing={2}>{jobs.map(job => <Card variant="outlined" key={job.job_id}><CardContent><Box sx={{display: 'flex', justifyContent: 'space-between', gap: 2}}><Typography sx={{overflowWrap: 'anywhere'}}>{job.filename}</Typography><Chip size="small" label={job.state} color={job.state === 'SUCCEEDED' ? 'success' : job.state === 'REJECTED' || job.state === 'FAILED' ? 'error' : 'default'} /></Box><Typography variant="caption" color="text.secondary">{new Date(job.created_at).toLocaleString()} {job.stage ? ` Â· ${job.stage}` : ''}</Typography>

      {job.state === 'SUBMISSION_UNKNOWN' && <Alert severity="warning" sx={{mt: 1}}>Upload confirmation is pending. This job is being checked automatically.</Alert>}

      {job.error && <Alert severity="error" sx={{mt: 1}}>{job.error.message}</Alert>}

      {active(job) && <Button onClick={()=>{faceVideoService.cancel(job.job_id).then(row=>{update([row,...jobsRef.current.filter(j=>j.job_id!==row.job_id)]);window.dispatchEvent(new Event('creditsUpdated'));}).catch(e=>setError((e as Error).message));}}>Cancel capture</Button>}

      {job.result_available && <Button data-nina-result={job.job_id} data-nina-cta="download" sx={{mt: 1}} disabled={!!downloadId} onClick={() => void download(job)}>{downloadId === job.job_id ? 'Downloadingâ€¦' : 'Download animation (.json)'}</Button>}

    </CardContent></Card>)}</Stack>



  </Container>;

};
